import {
	DeletedHubItemStatisticModel,
	getHubStatistics,
	HubItemStatisticKind,
	HubItemStatisticModel,
	HubItemStatisticsStatus,
	HubItemStatisticSummaryModel
} from "client/Api/Analytics/analyticsStatistics";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	daysRangeOptions,
	errorMessageKeyPerHubStatisticsKind,
	hubItemKindPerStatisticsKind,
	StatisticsFormatter,
	useStatisticsFormatters
} from "./common";
import {
	FormattedText,
	NoResults,
	TextSkeleton,
	Toolbar,
	useAlertDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { AnalyticsCard } from "./AnalyticsCard";
import {
	EMPTY_ARRAY
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { never } from "client_legacy/ndaw/errors";
import {
	getHubItemStatisticTitle,
	MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_DIALOG,
	useHubUsageDialog
} from "./HubUsageDialog";
import { useOpenHubItem } from "./useOpenHubItem";
import { AnalyticsTabLayout } from "./AnalyticsTabLayout";
import { AnalyticsShortList } from "./AnalyticsShortList";

const MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_CARD = 3;

export type HubTabProps = {
	selectedDaysRange: number;
	onSelectedDaysRangeChange: (selectedDaysRange: number) => void;
};

export const HubTab = (props: HubTabProps): ReactElement => {
	const {
		selectedDaysRange,
		onSelectedDaysRangeChange
	} = props;

	return (
		<AnalyticsTabLayout>
			<AnalyticsTabLayout.ToolbarContainer>
				<Toolbar>
					<Toolbar.InlineOptionSelector
						value={selectedDaysRange}
						onChange={onSelectedDaysRangeChange}
						options={daysRangeOptions}
						dropOverAlignment="left"
						menuTitle={localize("_Range")}
					/>
				</Toolbar>
			</AnalyticsTabLayout.ToolbarContainer>
			<AnalyticsTabLayout.Section title={localize("_Announcements")}>
				<AnalyticsTabLayout.CardList>
					<HubCard
						kind={HubItemStatisticKind.AnnouncementUniqueViews}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.AnnouncementComments}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.AnnouncementReactions}
						selectedDaysRange={selectedDaysRange}
					/>
				</AnalyticsTabLayout.CardList>
			</AnalyticsTabLayout.Section>

			<AnalyticsTabLayout.Section
				title={localize("_Events")}
			>
				<AnalyticsTabLayout.CardList>
					<HubCard
						kind={HubItemStatisticKind.EventAttendance}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.EventUniqueViews}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.EventComments}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.EventReactions}
						selectedDaysRange={selectedDaysRange}
					/>
				</AnalyticsTabLayout.CardList>
			</AnalyticsTabLayout.Section>
			<AnalyticsTabLayout.Section
				title={localize("_KnowledgeArticles")}
			>
				<AnalyticsTabLayout.CardList>
					<HubCard
						kind={HubItemStatisticKind.KnowledgeItemUniqueViews}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.KnowledgeItemTotalViews}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.KnowledgeItemComments}
						selectedDaysRange={selectedDaysRange}
					/>
					<HubCard
						kind={HubItemStatisticKind.KnowledgeItemReactions}
						selectedDaysRange={selectedDaysRange}
					/>
				</AnalyticsTabLayout.CardList>
			</AnalyticsTabLayout.Section>
		</AnalyticsTabLayout>
	);
};
HubTab.displayName = "HubTab";

type HubCardProps = {
	kind: HubItemStatisticKind;
	selectedDaysRange: number;
};

const HubCard = (props: HubCardProps): ReactElement => {
	const {
		kind,
		selectedDaysRange
	} = props;

	const [state, setState] = useState<CardState>({ status: "loading" });

	const alertManager: AlertManager = useAlerts();

	const formatter: StatisticsFormatter = useStatisticsFormatters();

	const [usageDialog, openUsageDialog] = useHubUsageDialog();
	const [hubItemDetails, openHubItemDetails] = useOpenHubItem();
	const [alertDialog, openAlertDialog] = useAlertDialog();
	const [reload, dataVersion] = useForceRerender();

	const openHubItemDetailsHandler = useCallback(
		(model: Exclude<HubItemStatisticModel, DeletedHubItemStatisticModel>): void => {
			if (model.status === HubItemStatisticsStatus.NoAccess) {
				alertManager.showError(localize("_YouDontHavePermissionsToViewThisItem"));
				return;
			}

			openHubItemDetails(
				model.status,
				hubItemKindPerStatisticsKind[kind],
				model.hubItemId
			);
		},
		[
			alertManager,
			kind,
			openHubItemDetails
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(dataVersion);
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setState({ status: "loading" });

					const statistics: HubItemStatisticSummaryModel = await getHubStatistics(
						selectedDaysRange,
						kind,
						MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_CARD,
						signal
					);

					setState({
						status: "ready",
						statistics
					});

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setState({ status: "error" });

					alertManager.showError(
						localize("_GeneralErrorMessage")
					);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			dataVersion,
			kind,
			selectedDaysRange
		]
	);

	if (state.status === "error") {
		return (
			<AnalyticsCard>
				<NoResults
					icon="chart-line"
					title={localize("_UnableToRetrieveData")}
					message={localize(errorMessageKeyPerHubStatisticsKind[kind])}
					height="fill"
					onRetry={reload}
				/>
			</AnalyticsCard>
		);
	}

	const showMoreInfoDialog = (): void => {
		openAlertDialog({
			title: localize(cardTitleKeyPerKind[kind]),
			children: (
				<FormattedText
					text={localize(moreInfoMessageKeyPerKind[kind])}
					thingsToReplace={EMPTY_ARRAY}
				/>
			),
			showCloseButton: true,
			trackingId: "hub-statistic-info-dialog"

		});
	};

	const renderTitle = (): ReactElement | string => {
		switch (state.status) {
			case "loading":
				return <TextSkeleton />;
			case "ready":
				return formatter(state.statistics.total);
			default:
				never(state);
		}
	};

	const renderContent = (): ReactElement => {
		switch (state.status) {
			case "loading":
				return (
					<AnalyticsShortList>
						<AnalyticsShortList.Item isLoading />
						<AnalyticsShortList.Item isLoading />
						<AnalyticsShortList.Item isLoading />
					</AnalyticsShortList>
				);
			case "ready":
				return (
					<AnalyticsShortList>
						{state.statistics
							.statistics
							.map((x, i) => {
								return (
									<HubStatisticItem
										key={i}
										statistic={x}
										onClick={openHubItemDetailsHandler}
									/>
								);
							})
						}
					</AnalyticsShortList>
				);
			default:
				never(state);
		}
	};

	return (
		<>
			<AnalyticsCard>
				<AnalyticsCard.Title
					title={state.status === "ready"
						? formatter(state.statistics.total, true)
						: undefined
					}
				>
					{renderTitle()}
				</AnalyticsCard.Title>
				<AnalyticsCard.InfoButton onClick={showMoreInfoDialog} />
				<AnalyticsCard.Subtitle>
					{localize(cardTitleKeyPerKind[kind])}
				</AnalyticsCard.Subtitle>
				<AnalyticsCard.Content>
					{renderContent()}
				</AnalyticsCard.Content>
				{state.status === "ready" &&
					state.statistics
						.statistics
						.length > 0 && (
							<AnalyticsCard.Footer
								title={
									state.statistics.totalNumberOfItems > MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_DIALOG
										? localize("_ViewTopN").replace("{{N}}", MAX_NUMBER_OF_ITEMS_TO_SHOW_IN_DIALOG.toString(10))
										: localize("_ViewAll")
								}
								onClick={() => openUsageDialog({kind, selectedDaysRange})}
							/>
						)
				}
			</AnalyticsCard>
			{usageDialog}
			{hubItemDetails}
			{alertDialog}
		</>
	);
};
HubCard.displayName = "HubCard";

type HubStatisticItemProps = {
	statistic: HubItemStatisticModel;
	onClick: (model: Exclude<HubItemStatisticModel, DeletedHubItemStatisticModel>) => void;
};

const HubStatisticItem = (props: HubStatisticItemProps): ReactElement => {
	const {
		statistic,
		onClick
	} = props;

	const name = useMemo(
		(): string => getHubItemStatisticTitle(statistic),
		[statistic]
	);

	const { value } = statistic;

	const onClickHandler = useCallback(
		(): void => {
			if (statistic.status === HubItemStatisticsStatus.Deleted) {
				throw new Error("Deleted item cannot be clicked");
			}

			onClick(statistic);
		},
		[onClick, statistic]
	);

	switch (statistic.status) {
		case HubItemStatisticsStatus.Deleted:
			return (
				<AnalyticsShortList.Item
					name={name}
					value={value}
					isDisabled
				/>
			);
		case HubItemStatisticsStatus.NoAccess:
		case HubItemStatisticsStatus.Published:
			return (
				<AnalyticsShortList.Item
					name={name}
					value={value}
					onClick={onClickHandler}
				/>
			);
		case HubItemStatisticsStatus.NotPublished:
			return (
				<AnalyticsShortList.Item
					name={name}
					value={value}
					onClick={onClickHandler}
					isDisabled
				/>
			);
		default:
			never(statistic);
	}
};
HubStatisticItem.displayName = "HubStatisticItem";

type CardState = {
	status: "loading";
} | {
	status: "error";
} | {
	status: "ready";
	statistics: HubItemStatisticSummaryModel;
};

const cardTitleKeyPerKind: { [key in HubItemStatisticKind]: LocalizationKey; } = {
	[HubItemStatisticKind.AnnouncementUniqueViews]: "_UniqueViews",
	[HubItemStatisticKind.AnnouncementComments]: "_Comments",
	[HubItemStatisticKind.AnnouncementReactions]: "_Reactions",
	[HubItemStatisticKind.EventAttendance]: "_SignUps",
	[HubItemStatisticKind.EventUniqueViews]: "_UniqueViews",
	[HubItemStatisticKind.EventComments]: "_Comments",
	[HubItemStatisticKind.EventReactions]: "_Reactions",
	[HubItemStatisticKind.KnowledgeItemUniqueViews]: "_UniqueViews",
	[HubItemStatisticKind.KnowledgeItemTotalViews]: "_TotalViews",
	[HubItemStatisticKind.KnowledgeItemComments]: "_Comments",
	[HubItemStatisticKind.KnowledgeItemReactions]: "_Reactions"
};

const moreInfoMessageKeyPerKind: { [key in HubItemStatisticKind]: LocalizationKey; } = {
	[HubItemStatisticKind.AnnouncementUniqueViews]: "_HubAnalyticsUniqueViewsMoreInfo",
	[HubItemStatisticKind.AnnouncementComments]: "_HubAnalyticsCommentsMoreInfo",
	[HubItemStatisticKind.AnnouncementReactions]: "_HubAnalyticsReactionsMoreInfo",
	[HubItemStatisticKind.EventAttendance]: "_HubAnalyticsSignUpsMoreInfo",
	[HubItemStatisticKind.EventUniqueViews]: "_HubAnalyticsUniqueViewsMoreInfo",
	[HubItemStatisticKind.EventComments]: "_HubAnalyticsCommentsMoreInfo",
	[HubItemStatisticKind.EventReactions]: "_HubAnalyticsReactionsMoreInfo",
	[HubItemStatisticKind.KnowledgeItemUniqueViews]: "_HubAnalyticsUniqueViewsMoreInfo",
	[HubItemStatisticKind.KnowledgeItemTotalViews]: "_HubAnalyticsTotalViewsMoreInfo",
	[HubItemStatisticKind.KnowledgeItemComments]: "_HubAnalyticsCommentsMoreInfo",
	[HubItemStatisticKind.KnowledgeItemReactions]: "_HubAnalyticsReactionsMoreInfo",
};